import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import SyncFusionDatePicker from "../Components/SyncFusionDateTimePicker/SyncFusionDatePicker";
import ProtocolGrid from "./ProtocolGrid";
import ProtocolFormModal from "./ProtocolFormModal";
import { protocolService } from "../../Services/protocol.service";
import { storage } from "../../Services/storage.service";
import { storageKeys } from "../../Constants/storage.key";
import "./protocols.css";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const CLIENT_FILTER_LOCKED_LEVEL_IDS = [10, 12, 13];

export default function ProtocolsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: "title", value: t("PROTOCOLS.TITLE") });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchParams] = useSearchParams();

  const userSession = storage.get(storageKeys.settings);
  const userLevelId = userSession?.levelId ? Number(userSession.levelId) : null;
  const isClientFilterLocked = CLIENT_FILTER_LOCKED_LEVEL_IDS.includes(userLevelId);
  const lockedClientName = userSession?.clientName ?? "";

  const [startDate, setStartDate] = useState(() => {
    const startDateParam = searchParams.get("startDate");
    if (startDateParam) {
      const parsed = new Date(startDateParam);
      if (!isNaN(parsed)) return parsed;
    }
    return new Date(Date.now() - SEVEN_DAYS_MS);
  });
  const [endDate, setEndDate] = useState(new Date());
  const [plate, setPlate] = useState("");
  const [clientName, setClientName] = useState(() =>
    isClientFilterLocked ? lockedClientName : searchParams.get("clientName") ?? ""
  );
  const [protocolIdFilter, setProtocolIdFilter] = useState("");
  const [protocolTypeId, setProtocolTypeId] = useState("TODOS");
  const [protocolSubTypeId, setProtocolSubTypeId] = useState("TODOS");
  const [protocolStatusId, setProtocolStatusId] = useState("TODOS");

  const [typeOptions, setTypeOptions] = useState([]);
  const [subtypeOptions, setSubtypeOptions] = useState([]);
  const [isLoadingSubtypes, setIsLoadingSubtypes] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);

  const [gridInfo, setGridInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toastParams, setToastParams] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalProtocol, setModalProtocol] = useState(null);

  const fetchProtocolsData = async (showSpinner) => {
    if (showSpinner) setIsLoading(true);
    try {
      const result = await protocolService.fetchProtocols({
        StartDate: startDate?.toISOString(),
        EndDate: endDate?.toISOString(),
        LicensePlate: plate,
        ClientName: clientName,
        ProtocolId: protocolIdFilter,
        ProtocolTypeId: protocolTypeId,
        ProtocolSubTypeId: protocolSubTypeId,
        ProtocolStatusId: protocolStatusId,
      });

      if (result === null) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("PROTOCOLS.TITLE"),
            body: t("REPORTS.ERROR_FETCHING_REPORT"),
            color: "Warning",
          })
        );
        setGridInfo([]);
      } else {
        setGridInfo(result);
      }
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  };

  useEffect(() => {
    protocolService.fetchProtocolTypes().then(setTypeOptions);
    protocolService.fetchProtocolStatusList().then(setStatusOptions);
    fetchProtocolsData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    setProtocolTypeId(typeId);
    setProtocolSubTypeId("TODOS");
    setSubtypeOptions([]);

    if (typeId === "TODOS") return;

    setIsLoadingSubtypes(true);
    protocolService
      .fetchProtocolSubtypes(typeId)
      .then(setSubtypeOptions)
      .finally(() => setIsLoadingSubtypes(false));
  };

  const handleAddNew = () => {
    setModalProtocol(null);
    setShowModal(true);
  };

  const handleViewDetails = useCallback((item) => {
    setModalProtocol(item);
    setShowModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setModalProtocol(null);
  };

  const totalCount = Array.isArray(gridInfo) ? gridInfo.length : 0;

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="row mb-2">
        <div className="col-12 d-flex justify-content-end">
          <span className="badge bg-label-primary" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.TOTAL_LABEL")}: {totalCount}
          </span>
        </div>
      </div>

      <div className="row mb-3 align-items-end g-3">
        <div className="col-md-3 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.START_DATE")}
          </label>
          <SyncFusionDatePicker
            id="protocols-start-date"
            value={startDate}
            onChange={setStartDate}
            step={30}
          />
        </div>
        <div className="col-md-3 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.END_DATE")}
          </label>
          <SyncFusionDatePicker
            id="protocols-end-date"
            value={endDate}
            onChange={setEndDate}
            step={30}
          />
        </div>
        <div className="col-md-2 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.PLATE")}
          </label>
          <input
            type="text"
            className="form-control"
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
          />
        </div>
        <div className="col-md-2 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.CLIENT")}
          </label>
          <input
            type="text"
            className="form-control"
            value={clientName}
            disabled={isClientFilterLocked}
            onChange={(e) => setClientName(e.target.value)}
          />
        </div>
        <div className="col-md-2 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.PROTOCOL_ID")}
          </label>
          <input
            type="text"
            className="form-control"
            value={protocolIdFilter}
            onChange={(e) => setProtocolIdFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="row mb-3 align-items-end g-3">
        <div className="col-md-4 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.TYPE")}
          </label>
          <select
            className="form-select"
            value={protocolTypeId}
            onChange={handleTypeChange}
          >
            <option value="TODOS">{t("PROTOCOLS.FILTERS.ALL")}</option>
            {typeOptions.map((option) => (
              <option key={option.protocolTypeId} value={option.protocolTypeId}>
                {option.protocolType}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.SUBTYPE")}
          </label>
          <select
            className="form-select protocol-subtype-select"
            value={protocolSubTypeId}
            disabled={protocolTypeId === "TODOS" || isLoadingSubtypes}
            onChange={(e) => setProtocolSubTypeId(e.target.value)}
          >
            <option value="TODOS">{t("PROTOCOLS.FILTERS.ALL")}</option>
            {subtypeOptions.map((option) => (
              <option key={option.protocolSubTypeId} value={option.protocolSubTypeId}>
                {option.protocolSubType}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4 col-6">
          <label className="form-label" style={{ fontSize: 10 }}>
            {t("PROTOCOLS.FILTERS.STATUS")}
          </label>
          <select
            className="form-select"
            value={protocolStatusId}
            onChange={(e) => setProtocolStatusId(e.target.value)}
          >
            <option value="TODOS">{t("PROTOCOLS.FILTERS.ALL")}</option>
            {statusOptions.map((option) => (
              <option key={option.protocolStatusId} value={option.protocolStatusId}>
                {option.protocolStatus}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-end align-items-center gap-2">
          <Button
            variant="outline-primary"
            className="text-nowrap"
            onClick={() => fetchProtocolsData(true)}
          >
            {t("PROTOCOLS.FILTERS.SEARCH_BUTTON")}
          </Button>
          <Button variant="primary" className="text-nowrap" onClick={handleAddNew}>
            {t("PROTOCOLS.ADD_BUTTON")}
          </Button>
        </div>
      </div>

      {gridInfo && <ProtocolGrid gridData={gridInfo} onViewDetails={handleViewDetails} />}
      <Spinner visible={isLoading} />

      <ProtocolFormModal
        show={showModal}
        protocol={modalProtocol}
        handleClose={handleCloseModal}
        onSaved={() => fetchProtocolsData(false)}
      />
    </div>
  );
}
